import classNames from 'classnames';
import { useAtomValue } from 'jotai';

import type { Appointment } from '../../../../react-query/useAppointments';
import { usePreferences } from '../../../../react-query/usePreferences';
import type {
  TProcedureStatus,
  TVisitSummary,
} from '../../../../react-query/useVisitSummaries';
import { selectQuadrantsAtom, selectedCountsFilterAtom } from '../../atoms';

import { useIsBasicOdontogram, useVisualizationProcedure } from './hooks';
import { QuadrantHover } from './quadrantHover/quadrantHover';
import { QuadrantTooth } from './quadrantTooth/quadrantTooth';
import { type ToothStatus } from './Tooth';
import type { TDisplayOrderQuadrantAbbreviations } from './types';
import { quadrantToNumber } from './useToothSelection';
import {
  getIsVisualizationFilteredOut,
  isLowerQuadrant,
  isUpperQuadrant,
} from './utils';

const SpotlightStatuses: readonly TProcedureStatus[] = [
  'recommended',
  'unscheduled',
] as const;

export const Quadrant = ({
  activeImageId,
  quadrant,
  toothStatuses,
  visitSummary,
  appointment,
  selectedStatuses,
  module,
  scaleFactor,
}: {
  activeImageId?: string;
  quadrant: TDisplayOrderQuadrantAbbreviations;
  toothStatuses: ToothStatus[];
  visitSummary?: TVisitSummary;
  appointment?: Appointment;
  selectedStatuses?: TProcedureStatus[];
  module?: 'image-viewer' | 'patient-list';
  scaleFactor?: number;
}) => {
  const isBasicOdontogram = useIsBasicOdontogram();
  const { data: prefs } = usePreferences();
  const selectedQuadrants = useAtomValue(selectQuadrantsAtom);
  const selectedCountsFilter = useAtomValue(selectedCountsFilterAtom);

  const quadrantProcedures = visitSummary?.quadrants?.find(
    (quad) => quad.quadrant === quadrant,
  )?.procedures;
  const visualizationProcedure = useVisualizationProcedure(
    quadrantProcedures,
    selectedStatuses,
    visitSummary?.isPerioPatient,
  );

  const getQuadrantBorderColor = () => {
    const isDarkMode = module === 'image-viewer';

    if (isBasicOdontogram) {
      if (visualizationProcedure?.status === 'recommended') {
        return isDarkMode ? 'vai-border-violet-700' : 'vai-border-purple-700';
      }
      return isDarkMode ? 'vai-border-slate-800' : 'vai-border-zinc-200';
    }

    switch (visualizationProcedure?.status) {
      case 'recommended':
        return isDarkMode ? 'vai-border-violet-700' : 'vai-border-purple-700';
      case 'unscheduled':
        return 'vai-border-red-500';
      case 'scheduled_future':
      case 'scheduled_today':
        return isDarkMode ? 'vai-border-slate-500' : 'vai-border-slate-400';
      case 'completed':
        return isDarkMode ? 'vai-border-sapphire-500' : 'vai-border-aqua-400';
      default:
        return isDarkMode ? 'vai-border-slate-800' : 'vai-border-zinc-200';
    }
  };

  const isFilteredOut = getIsVisualizationFilteredOut(
    visualizationProcedure?.name,
    visualizationProcedure?.status,
    selectedCountsFilter,
    module,
  );

  return (
    <div
      className={classNames(
        'vai-py-2 vai-rounded-[2px] vai-flex vai-border vai-border-solid vai-relative vai-overflow-hidden',
        getQuadrantBorderColor(),
        {
          'vai-align-bottom vai-pb-0': isUpperQuadrant(quadrant),
          'vai-align-top vai-pt-0': isLowerQuadrant(quadrant),
          'vai-bg-violet-500': selectedQuadrants.includes(
            quadrantToNumber[quadrant],
          ),
          'vai-border-opacity-35': isFilteredOut,
          'vai-border-[2.75px]': !!visualizationProcedure?.status,
        },
      )}
      key={quadrant}
      data-cy={`quadrant-${quadrant}`}
      data-testid={`quadrant-${quadrant}`}
    >
      <QuadrantHover
        activeImageId={activeImageId}
        appointment={appointment}
        module={module}
        visitSummary={visitSummary}
        quadrant={quadrant}
        visualizationStatus={visualizationProcedure?.status}
      >
        <div className="vai-absolute vai-h-full vai-w-full vai-top-0 vai-z-0" />
      </QuadrantHover>
      {toothStatuses.map((toothStatus, index) => {
        const isSpotlightProcedure =
          prefs.spotlightProcedure === toothStatus.type;
        const isSpotlightStatus =
          !!toothStatus.status &&
          SpotlightStatuses.includes(toothStatus.status);
        return (
          <QuadrantTooth
            activeImageId={activeImageId}
            isSpotlight={isSpotlightProcedure && isSpotlightStatus}
            key={toothStatus.toothId}
            toothStatus={toothStatus}
            currentToothIndex={index}
            lastToothIndex={toothStatuses.length - 1}
            quadrant={quadrant}
            visitSummary={visitSummary}
            appointment={appointment}
            module={module}
            scaleFactor={scaleFactor}
          />
        );
      })}
    </div>
  );
};
